import { request } from "hooks/useOkoApi/useOkoApi";
import { logError, LogCategory } from "utils/sentryLogger";

export const isEndpointMissing = (e) => {
  const status = e?.status;
  const detail = `${e?.detail || ""} ${e?.message || ""}`.toLowerCase();
  return (
    status === 404 ||
    status === 405 ||
    detail.includes("404") ||
    detail.includes("not found") ||
    detail.includes("method not allowed")
  );
};

const log = (message, e) =>
  logError(message, e, { category: LogCategory.ACTIVITY, activityType: "skills_profile" });

// UUID v4 format checker
export const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isValidUUID = (id) => typeof id === "string" && UUID_REGEX.test(id.trim());

/**
 * Parses raw newline-separated (or whitespace-separated) session IDs from a textarea.
 * Returns { parsedCount, validUnique, duplicatesCount, invalidCount, items }
 */
export const parseSessionIds = (rawText) => {
  if (!rawText || !rawText.trim()) {
    return {
      parsedCount: 0,
      validUnique: [],
      duplicatesCount: 0,
      invalidCount: 0,
      items: [],
    };
  }

  const lines = rawText
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const seen = new Set();
  const items = [];
  const validUnique = [];
  let duplicatesCount = 0;
  let invalidCount = 0;

  lines.forEach((id) => {
    if (!isValidUUID(id)) {
      invalidCount += 1;
      items.push({ id, status: "unknown" });
    } else if (seen.has(id.toLowerCase())) {
      duplicatesCount += 1;
      items.push({ id, status: "dup" });
    } else {
      seen.add(id.toLowerCase());
      validUnique.push(id);
      items.push({ id, status: "valid" });
    }
  });

  return {
    parsedCount: lines.length,
    validUnique,
    duplicatesCount,
    invalidCount,
    items,
  };
};

// GET admin/skills-profile/batches
export const fetchBatches = async () => {
  try {
    const res = await request("admin/skills-profile/batches", { method: "GET" });
    return res?.batches || [];
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch skills profile batches", e);
    throw e;
  }
};

// Backward-compatible alias
export const fetchSkillsBatches = fetchBatches;

// POST admin/skills-profile/batches
export const createBatch = async ({ name, description = null }) => {
  try {
    return await request("admin/skills-profile/batches", {
      method: "POST",
      payload: { name, description },
    });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to create skills profile batch", e);
    throw e;
  }
};

// POST admin/skills-profile/batches/{batch_id}/items
export const appendBatchItems = async (batchId, sessionIds) => {
  try {
    return await request(`admin/skills-profile/batches/${batchId}/items`, {
      method: "POST",
      payload: { session_ids: sessionIds },
    });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log(`Failed to append items to batch ${batchId}`, e);
    throw e;
  }
};

// GET admin/skills-profile/runs
export const fetchPromptRuns = async ({ promptId = null } = {}) => {
  try {
    const query = promptId ? `?prompt_id=${encodeURIComponent(promptId)}` : "";
    const res = await request(`admin/skills-profile/runs${query}`, { method: "GET" });
    return res?.runs || [];
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch prompt runs", e);
    throw e;
  }
};

// GET admin/skills-profile/prompts
export const fetchPromptDefinitions = async () => {
  try {
    const res = await request("admin/skills-profile/prompts", { method: "GET" });
    return res?.prompts || [];
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch prompt definitions", e);
    throw e;
  }
};

// POST admin/skills-profile/prompts
export const createPromptDefinition = async ({
  name,
  description = null,
  provider,
  model,
  temperature,
  reasoningLevel = null,
  promptText,
}) => {
  try {
    return await request("admin/skills-profile/prompts", {
      method: "POST",
      payload: {
        name,
        description,
        provider,
        model,
        temperature,
        reasoning_level: reasoningLevel,
        prompt_text: promptText,
      },
    });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to create prompt definition", e);
    throw e;
  }
};

// POST admin/skills-profile/runs
export const triggerPromptRun = async ({ promptId, batchIds, costCeilingUsd = null, displayName = null }) => {
  try {
    return await request("admin/skills-profile/runs", {
      method: "POST",
      payload: {
        prompt_id: promptId,
        batch_ids: batchIds,
        cost_ceiling_usd: costCeilingUsd,
        display_name: displayName,
      },
    });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to trigger prompt run", e);
    throw e;
  }
};

// GET admin/skills-profile/runs/{run_id}
export const fetchPromptRun = async (runId) => {
  try {
    return await request(`admin/skills-profile/runs/${runId}`, { method: "GET" });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log(`Failed to fetch prompt run ${runId}`, e);
    throw e;
  }
};

// PATCH admin/skills-profile/runs/{run_id}
export const renamePromptRun = async (runId, displayName) => {
  try {
    return await request(`admin/skills-profile/runs/${runId}`, {
      method: "PATCH",
      payload: { display_name: displayName },
    });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log(`Failed to rename prompt run ${runId}`, e);
    throw e;
  }
};

// DELETE admin/skills-profile/runs/{run_id}
export const deletePromptRun = async (runId) => {
  try {
    return await request(`admin/skills-profile/runs/${runId}`, {
      method: "DELETE",
    });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log(`Failed to delete prompt run ${runId}`, e);
    throw e;
  }
};

/**
 * Get ordered sessions in a batch with review progress.
 * GET /admin/skills-profile/batches/{batch_id}/queue
 */
export const fetchSkillsBatchQueue = async (batchId) => {
  try {
    return await request(`admin/skills-profile/batches/${batchId}/queue`, { method: "GET" });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch skills batch queue", e);
    throw e;
  }
};

/**
 * Get hydrated transcript for a session.
 * GET /admin/skills-profile/session/{session_id}/transcript
 */
export const fetchSessionTranscript = async (sessionId) => {
  try {
    return await request(`admin/skills-profile/session/${sessionId}/transcript`, { method: "GET" });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch skills session transcript", e);
    throw e;
  }
};

/**
 * Get live product tags for a session (SessionTaggedQuote rows, read-only).
 * GET /admin/skills-profile/session/{session_id}/product-tags
 */
export const fetchProductTags = async (sessionId) => {
  try {
    return await request(`admin/skills-profile/session/${sessionId}/product-tags`, { method: "GET" });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch product tags", e);
    throw e;
  }
};

/**
 * Get finished prompt runs covering a session for the LLM Tags dropdown.
 * GET /admin/skills-profile/session/{session_id}/runs
 */
export const fetchSessionRuns = async (sessionId) => {
  try {
    return await request(`admin/skills-profile/session/${sessionId}/runs`, { method: "GET" });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch session prompt runs", e);
    throw e;
  }
};

/**
 * Get active vocabulary (skills and tags).
 * GET /admin/skills-profile/vocabulary
 */
export const fetchVocabulary = async () => {
  try {
    return await request("admin/skills-profile/vocabulary", { method: "GET" });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch skills vocabulary", e);
    throw e;
  }
};

/**
 * Get existing human reviews for a session created by the current evaluator.
 * GET /admin/skills-profile/reviews?session_id={session_id}
 */
export const fetchSessionReviews = async (sessionId) => {
  try {
    return await request(`admin/skills-profile/reviews?session_id=${sessionId}`, { method: "GET" });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch session human reviews", e);
    throw e;
  }
};

/**
 * Upsert a human quote tagging review (draft-capable).
 * PUT /admin/skills-profile/reviews
 */
export const saveQuoteReview = async ({ sessionId, quoteTimestamp, quote, skill, tags, reasons }) => {
  try {
    return await request("admin/skills-profile/reviews", {
      method: "PUT",
      payload: {
        session_id: sessionId,
        quote_timestamp: quoteTimestamp,
        quote,
        skill: skill || null,
        tags: tags === null ? null : tags || [],
        reasons: reasons === null ? null : reasons || {},
      },
    });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to save quote review", e);
    throw e;
  }
};

/**
 * Get per-quote LLM results for a prompt run, optionally filtered by session.
 * GET /admin/skills-profile/runs/{run_id}/results(?session_id={session_id})
 */
export const fetchRunResults = async (runId, sessionId = null) => {
  try {
    const query = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : "";
    return await request(`admin/skills-profile/runs/${runId}/results${query}`, {
      method: "GET",
    });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch prompt run results", e);
    throw e;
  }
};

/**
 * Get challenge question details for transcript question rendering.
 * GET /admin/skills-profile/questions/{challenge_id}
 */
export const fetchChallengeQuestion = async (challengeId) => {
  try {
    return await request(`admin/skills-profile/questions/${encodeURIComponent(challengeId)}`, {
      method: "GET",
    });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to fetch challenge question", e);
    throw e;
  }
};

/**
 * Upsert an evaluation review of an LLM tag result (draft-capable).
 * PUT /admin/skills-profile/llm-reviews
 */
export const saveLlmReview = async ({ resultId, agreement, confidence, reason }) => {
  try {
    return await request("admin/skills-profile/llm-reviews", {
      method: "PUT",
      payload: {
        result_id: resultId,
        agreement: agreement || null,
        confidence: confidence || null,
        reason: reason || null,
      },
    });
  } catch (e) {
    if (isEndpointMissing(e)) throw e;
    log("Failed to save LLM review", e);
    throw e;
  }
};
